import PdfRendererController from "./pdf_renderer_controller";

// Connects to data-controller="document-tagging"
export default class extends PdfRendererController {
  static targets = [
    "pdfModal",
    "pagePreview",
    "taggingButton",
    "canvas",
    "draggableField",
    "form",
    "tagSettings",
  ];
  static classes = ["hidden"];

  connect() {
    super.connect();
    this.pdf_bytes = null;
    this.keyboard_positioned_element = null;
    this.mouse_positioned_element = null;
    this.configurable_element = null;
    this.clicked_on_toolbox_field = false;
    this.existing_tagging_data = null;
    this.isSubmitting = false;
    this.templateNameToSave = null; // Store template name from modal

    this.handle_key_down = this.handleKeyDown.bind(this);

    document.addEventListener("keydown", this.handle_key_down);
    this.handle_click();

    this.addInput("text", "customId", "custom ID");
    this.addInput("text", "placeholder", "placeholder text");

    this.load_tagging_data();
    this.load_existing_pdfs();
  }

  addInput(type, name, placeholder) {
    const inputId = `input-${name}`;

    if (this.tagSettingsTarget.querySelector(`#${inputId}`)) {
      return;
    }

    const input = document.createElement("input");
    input.type = type;
    input.name = name;
    input.id = inputId;
    input.dataset.annotationSetting = name;
    const label = document.createElement("label");
    label.innerText = placeholder;

    const div = document.createElement("div");
    div.appendChild(label);
    div.appendChild(input);
    const form = this.tagSettingsTarget.querySelector("form");
    form.appendChild(div);

    input.addEventListener("input", (event) => {
      const input_text = event.target.value;
      this.configurable_element.dataset[name] = input_text;
      if (
        this.configurable_element.dataset["draggableFieldType"] === "custom" &&
        input.name === "placeholder"
      ) {
        this.configurable_element.innerText = input_text || "Custom";
      }
    });
  }

  disconnect() {
    document.removeEventListener("keydown", this.handle_key_down);
  }

  handleFormSubmit(event) {
    // If we're already submitting, don't intercept
    if (this.isSubmitting) {
      return;
    }

    // Check if there's tagging data
    const taggingDataField = this.formTarget.querySelector('input[name="document_tagging_data"]');
    const hasTaggingData = taggingDataField && taggingDataField.value;

    // If no tagging data, let the form submit normally
    if (!hasTaggingData) {
      return;
    }

    // If we have tagging data, check if we should save as template
    // The templateNameToSave was set when the user clicked Save in the tagging modal
    if (this.templateNameToSave !== null && this.templateNameToSave !== undefined) {
      event.preventDefault();
      this.submitFormWithTemplate(this.templateNameToSave);
    }
    // Otherwise, let the form submit normally (user chose not to save template)
  }

  submitFormWithTemplate(templateName) {
    // Add or remove the template fields
    let saveTemplateInput = this.formTarget.querySelector('input[name="save_as_template"]');
    let templateNameInput = this.formTarget.querySelector('input[name="template_name"]');

    if (templateName) {
      // Add hidden fields for template saving
      if (!saveTemplateInput) {
        saveTemplateInput = document.createElement("input");
        saveTemplateInput.type = "hidden";
        saveTemplateInput.name = "save_as_template";
        this.formTarget.appendChild(saveTemplateInput);
      }
      saveTemplateInput.value = "true";

      if (!templateNameInput) {
        templateNameInput = document.createElement("input");
        templateNameInput.type = "hidden";
        templateNameInput.name = "template_name";
        this.formTarget.appendChild(templateNameInput);
      }
      templateNameInput.value = templateName;
    } else {
      // Remove template fields if they exist
      if (saveTemplateInput) {
        saveTemplateInput.remove();
      }
      if (templateNameInput) {
        templateNameInput.remove();
      }
    }

    // Set flag to prevent re-intercepting the submission
    this.isSubmitting = true;

    // Submit the form
    this.formTarget.requestSubmit();
  }

  handle_click() {
    this.pdfModalTarget.addEventListener("click", (event) => {
      if (event.target.closest("#tag-settings")) {
        return;
      }
      if (event.target.classList.contains("draggable-field")) {
        this.setup_field_settings(this.mouse_positioned_element);
      } else {
        if (!this.clicked_on_toolbox_field) {
          if (this.is_resizing) {
            this.is_resizing = false;
            return;
          }
          this.hide_settings();
        }
        this.clicked_on_toolbox_field = false;
      }
      const selected_dragable_fields = document.querySelectorAll(
        ".draggable-field.positioned.selected",
      );
      selected_dragable_fields.forEach((d) => {
        this.position_keyboard_adjusted_element(d);
      });
      this.keyboard_positioned_element = null;
    });
  }

  load_tagging_data() {
    if (!this.hasExistingPdfsTarget) return;

    const tagging_data_json = this.existingPdfsTarget.dataset.existingTaggingData;
    if (!tagging_data_json) return;

    try {
      this.existing_tagging_data = JSON.parse(tagging_data_json);
    } catch (e) {
      console.error("Failed to parse existing tagging data:", e);
      this.existing_tagging_data = null;
    }
  }

  // Override hook method to enable tagging button when PDF is loaded
  on_pdf_loaded(file) {
    this.taggingButtonTarget.disabled = false;
    this.pdf_bytes = file;
  }

  async on_page_rendered(_page, canvas, viewport, page_num, pdf_wrapper, scale) {
    let total_height = parseFloat(pdf_wrapper.dataset.totalHeight || "0");

    const marginTop = parseFloat(getComputedStyle(canvas).marginTop);

    if (total_height === 0) {
      total_height = marginTop;
    }

    const container_width = parseFloat(getComputedStyle(this.canvasTarget).width);
    const padding = parseFloat(getComputedStyle(this.canvasTarget).padding);
    const border_width = parseFloat(getComputedStyle(this.canvasTarget).borderWidth);

    const adjusted_width = container_width - (2 * padding + 2 * border_width);

    const { overlay, calculated_height } = this.create_overlay(
      viewport,
      adjusted_width,
      page_num,
      total_height,
      scale,
    );

    total_height += calculated_height + 2 * marginTop;
    pdf_wrapper.dataset.totalHeight = total_height.toString();

    pdf_wrapper.appendChild(overlay);

    // Restore existing tags for this page
    this.restore_tags_for_page(overlay, pdf_wrapper.dataset.fileName, page_num);
  }

  create_overlay(viewport, container_width, pageNum, total_height, scale) {
    const root_font_size = this.get_root_font_size();
    const div_overlay = document.createElement("div");
    const ratio = viewport.width / viewport.height;
    const ratio_viewport_width_to_container_width = viewport.width / container_width;
    const calculated_height = container_width / ratio;

    div_overlay.style.height = `${calculated_height / root_font_size}rem`;
    div_overlay.style.top = `${total_height / root_font_size}rem`;
    div_overlay.classList.add("canvas_overlay");
    div_overlay.dataset.pageNumber = pageNum;
    div_overlay.dataset.viewportWidth = `${viewport.width}`;
    div_overlay.dataset.viewportHeight = `${viewport.height}`;
    div_overlay.dataset.ratio = `${ratio_viewport_width_to_container_width}`;
    div_overlay.dataset.pdfScale = `${scale}`;

    return { overlay: div_overlay, calculated_height: calculated_height };
  }

  // Display PDF Modal
  show_pdf_modal(event) {
    event.preventDefault();

    this.pdfModalTarget.classList.remove("hidden");
    this.pdfModalTarget.addEventListener("click", this.handleModalClick);

    this.trap_focus_in_modal();
  }

  close_modal() {
    this.pdfModalTarget.classList.add("hidden");

    this.pdfModalTarget.removeEventListener("click", this.handleModalClick);
  }

  trap_focus_in_modal() {
    const sections = ["page-preview", "pdf-viewer", "toolbox"];
    let focusableElements = [];

    sections.forEach((id) => {
      const section = document.getElementById(id);
      if (section) focusableElements.push(section);
    });

    const closeButton = this.pdfModalTarget.querySelector(
      "[data-action='click->document-tagging#close_modal']",
    );
    const saveButton = this.pdfModalTarget.querySelector(
      "[data-action='click->document-tagging#capture_field_data']",
    );
    if (saveButton) focusableElements.push(saveButton);
    if (closeButton) focusableElements.push(closeButton);

    if (focusableElements.length === 0) return;

    let currentIndex = 0;

    this.pdfModalTarget.addEventListener("keydown", (event) => {
      if (event.key === "Tab") {
        const is_configurable = event.target === this.configurable_element;
        if (is_configurable) {
          if (event.target.classList.contains("selected")) {
            this.position_keyboard_adjusted_element(event.target);
          }
          this.tagSettingsTarget.focus();
          return;
        }
        event.preventDefault();
        this.hide_settings();
        currentIndex =
          (currentIndex + (event.shiftKey ? -1 : 1) + focusableElements.length) %
          focusableElements.length;
        focusableElements[currentIndex].focus();
        this.active_field = null;
      }
    });
  }

  handleKeyDown(event) {
    if (event.key === "Escape") {
      if (!this.pdfModalTarget.classList.contains("hidden")) {
        this.close_modal();
      }
    }

    const activeElement = document.activeElement;
    if (!activeElement) return;

    const pdfViewer = document.querySelector("#pdf-viewer");
    const toolbox = document.querySelector("#toolbox");

    if (pdfViewer && pdfViewer.contains(activeElement)) {
      this.handleArrowNavigation(event, activeElement, pdfViewer);
    } else if (this.tagSettingsTarget && this.tagSettingsTarget.contains(activeElement)) {
      const form = this.tagSettingsTarget.querySelector("form");
      if (form && form.contains(activeElement)) {
        this.handleFormNavigation(event, activeElement, form);
      }
    } else if (toolbox && toolbox.contains(activeElement)) {
      this.handleArrowNavigation(event, activeElement, toolbox);
    }
  }

  handleFormNavigation(event, activeElement, form) {
    const inputs = [...form.querySelectorAll("input, textarea, select")];
    if (inputs.length === 0) return;

    const currentIndex = inputs.indexOf(activeElement);

    if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextIndex = currentIndex + 1;
      if (nextIndex < inputs.length) {
        inputs[nextIndex].focus();
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const prevIndex = currentIndex - 1;
      if (prevIndex >= 0) {
        inputs[prevIndex].focus();
      }
    }
  }

  handleArrowNavigation(event, section, active_element_parent) {
    const items = [...active_element_parent.querySelectorAll(".draggable-field")];
    if (items.length === 0) return;

    let currentIndex = items.findIndex((item) => {
      return item === section || 0;
    });

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!section.classList.contains("selected")) {
        const nextIndex = (currentIndex + 1) % items.length;
        this.keyboard_positioned_element = items[nextIndex];
        items[nextIndex].focus();
        this.setup_field_settings(this.keyboard_positioned_element);
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!section.classList.contains("selected")) {
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        this.keyboard_positioned_element = items[prevIndex];
        items[prevIndex].focus();
        this.setup_field_settings(this.keyboard_positioned_element);
      }
    }
  }

  // Mouse and Touch Positioning of Draggable Elements on Canvas
  start_drag(event) {
    if (!this.is_valid_drag_event(event)) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    if (!this.is_valid_drag_event(event)) return;

    const { client_x, client_y } = this.get_pointer_coordinates(event);

    this.set_positioned_element(event, "mouse");
    this.mouse_positioned_element.classList.add("positioned");

    this.calculate_offsets(event, client_x, client_y);
    this.position_dragged_field(client_x, client_y);
    this.pdfModalTarget.appendChild(this.mouse_positioned_element);

    this.canvasTarget.style.overflowY = "scroll";
    this.canvasTarget.style.scrollbarWidth = "none";
    this.canvasTarget.style.msOverflowStyle = "none";

    this.mouse_positioned_element.focus();

    this.add_drag_event_listeners();
  }

  // settings display and data updating
  setup_field_settings(element) {
    this.configurable_element = element;

    this.updateTagSettings();
    this.show_settings();
  }

  show_settings() {
    if (getComputedStyle(this.tagSettingsTarget).left != "0px") {
      this.tagSettingsTarget.style.left = 0;
    }
  }

  hide_settings() {
    if (getComputedStyle(this.tagSettingsTarget).left == "0px") {
      this.tagSettingsTarget.style.left = "16.67rem";

      this.configurable_element = null;
      this.tagSettingsTarget.querySelector("p").textContent = "";
    }
  }

  updateTagSettings() {
    if (!this.configurable_element) return;

    const tagSettingsElement = this.tagSettingsTarget.querySelector("div");
    if (!tagSettingsElement) return;

    const fieldType = this.configurable_element.dataset.draggableFieldType;
    let pageElement = this.configurable_element.closest("[data-page-number]");

    this.set_settings_input_data();

    let unpositioned_element = false;
    const { left, top } = this.configurable_element.getBoundingClientRect();
    if (!pageElement) {
      pageElement = this.find_drop_target_overlay(left, top);
      unpositioned_element = true;
    }
    if (!pageElement) return;

    const file_name = this.find_drop_target_pdf_wrapper(left, top).dataset["fileName"];

    const pageNumber = pageElement.dataset.pageNumber;
    const sameTypeElements = Array.from(
      pageElement.querySelectorAll(`[data-draggable-field-type='${fieldType}']`),
    );
    let index;
    if (sameTypeElements.length === 0 && unpositioned_element) {
      index = 1;
    } else if (sameTypeElements.length > 0 && unpositioned_element) {
      index = sameTypeElements.length + 1;
    } else {
      index = sameTypeElements.indexOf(this.configurable_element) + 1;
    }

    tagSettingsElement.innerHTML = `<p class="file">${file_name}</p><p>Page ${pageNumber}, ${fieldType} ${index}</p>`;
  }

  set_settings_input_data() {
    const field_data = this.configurable_element.dataset;
    const tagging_inputs = [...this.tagSettingsTarget.querySelectorAll("input")];
    tagging_inputs.forEach((input) => {
      const inputName = input.name;
      if (inputName in field_data) {
        input.value = field_data[inputName];
      }
    });
  }

  is_valid_drag_event(event) {
    let is_touch = event.type.startsWith("touch");
    return (event.button === 0 || is_touch) && event.target.classList.contains("draggable-field");
  }

  calculate_offsets(event, client_x, client_y) {
    let is_touch = event.type.startsWith("touch");
    const target_rect = event.target.getBoundingClientRect();

    this.offset_x = is_touch ? client_x - target_rect.left : event.offsetX;
    this.offset_y = is_touch ? client_y - target_rect.top : event.offsetY;
  }

  position_dragged_field(client_x, client_y) {
    const root_font_size = this.get_root_font_size();
    this.mouse_positioned_element.style.left = `${(client_x - this.offset_x) / root_font_size}rem`;
    this.mouse_positioned_element.style.top = `${(client_y - this.offset_y) / root_font_size}rem`;
  }

  add_drag_event_listeners() {
    document.addEventListener("mousemove", this.move_element);
    document.addEventListener("touchmove", this.move_element, { passive: false });
    document.addEventListener("mouseup", this.drop_element);
    document.addEventListener("touchend", this.drop_element);
  }

  move_element = (event) => {
    if (!this.mouse_positioned_element) return;

    if (event.cancelable) {
      event.preventDefault();
    }

    const root_font_size = this.get_root_font_size();

    let is_touch = event.type.startsWith("touch");
    let page_x = is_touch ? event.touches[0].pageX : event.pageX;
    let page_y = is_touch ? event.touches[0].pageY : event.pageY;

    const { new_left, new_top } = this.calculate_new_field_position(page_x, page_y, root_font_size);

    this.mouse_positioned_element.focus();

    this.apply_new_position(new_left, new_top);
  };

  calculate_new_field_position(page_x, page_y, root_font_size) {
    const dragged_element_rect = this.mouse_positioned_element.getBoundingClientRect();
    const parent_rect = this.mouse_positioned_element.parentElement.getBoundingClientRect();

    let new_left = (page_x - this.offset_x) / root_font_size;
    let new_top = (page_y - this.offset_y) / root_font_size;

    const max_left = (parent_rect.width - dragged_element_rect.width) / root_font_size;
    const max_top = (parent_rect.height - dragged_element_rect.height) / root_font_size;

    return {
      new_left: Math.max(0, Math.min(new_left, max_left)),
      new_top: Math.max(0, Math.min(new_top, max_top)),
    };
  }

  apply_new_position(newLeft, newTop) {
    this.mouse_positioned_element.style.left = `${newLeft}rem`;
    this.mouse_positioned_element.style.top = `${newTop}rem`;
  }

  get_drop_coordinates(event) {
    const is_touch = event.type.startsWith("touch");
    return {
      client_x: is_touch ? event.changedTouches[0].clientX : event.clientX,
      client_y: is_touch ? event.changedTouches[0].clientY : event.clientY,
    };
  }

  find_drop_target_overlay(client_x, client_y) {
    const divs = document.elementsFromPoint(client_x, client_y);
    const overlay = divs.find((d) => d.classList.contains("canvas_overlay"));

    if (!overlay || !this.canvasTarget || !this.canvasTarget.contains(overlay)) {
      return null;
    }
    return overlay;
  }

  find_drop_target_pdf_wrapper(client_x, client_y) {
    const divs = document.elementsFromPoint(client_x, client_y);
    const wrapper = divs.find((d) => d.classList.contains("pdf-wrapper"));

    if (!wrapper || !this.canvasTarget || !this.canvasTarget.contains(wrapper)) {
      return null;
    }
    return wrapper;
  }

  calculate_constrained_position(client_x, client_y, overlay) {
    const overlay_rect = overlay.getBoundingClientRect();
    const dragged_element_rect = this.mouse_positioned_element.getBoundingClientRect();

    const relative_x = client_x - overlay_rect.left - this.offset_x;
    const relative_y = client_y - overlay_rect.top - this.offset_y;

    const max_left = overlay_rect.width - dragged_element_rect.width;
    const max_top = overlay_rect.height - dragged_element_rect.height;

    return {
      x: Math.max(0, Math.min(relative_x, max_left)),
      y: Math.max(0, Math.min(relative_y, max_top)),
    };
  }

  apply_final_element_styles(element, position) {
    const root_font_size = this.get_root_font_size();

    element.style.position = "absolute";
    element.style.left = `${position.x / root_font_size}rem`;
    element.style.top = `${position.y / root_font_size}rem`;
    element.style.opacity = "1";
    element.style.cursor = "grab";
  }

  drop_element = (event) => {
    if (!this.mouse_positioned_element) return;
    if (event.cancelable) {
      event.preventDefault();
    }

    const { client_x, client_y } = this.get_drop_coordinates(event);
    const target_overlay = this.find_drop_target_overlay(client_x, client_y);

    if (!target_overlay) {
      this.cleanup_drag();
      this.hide_settings();
      return;
    }

    const position = this.calculate_constrained_position(client_x, client_y, target_overlay);

    this.apply_final_element_styles(this.mouse_positioned_element, position);
    this.make_element_resizable(this.mouse_positioned_element);
    this.mouse_positioned_element.classList.remove("selected");

    this.cleanup_drag();
    target_overlay.appendChild(this.mouse_positioned_element);
    this.sort_draggable_fields(target_overlay);
    this.setup_field_settings(this.mouse_positioned_element);
    this.mouse_positioned_element.focus();
  };

  set_positioned_element(event, create_type) {
    let new_field;
    if (event.target.classList.contains("positioned")) {
      new_field = event.target;
    } else {
      new_field = event.target.cloneNode(true);
      this.clicked_on_toolbox_field = true;
    }

    if (create_type === "keyboard") {
      this.keyboard_positioned_element = new_field;
    } else if (create_type === "mouse") {
      this.mouse_positioned_element = new_field;
      this.mouse_positioned_element.classList.add("selected");
      this.mouse_positioned_element.focus();
    }
  }

  cleanup_drag() {
    document.removeEventListener("mousemove", this.move_element);
    document.removeEventListener("mouseup", this.drop_element);
    document.removeEventListener("touchmove", this.move_element);
    document.removeEventListener("touchend", this.drop_element);

    const has_dragged_element = this.pdfModalTarget.contains(this.mouse_positioned_element);

    if (has_dragged_element) {
      this.pdfModalTarget.removeChild(this.mouse_positioned_element);
    }

    this.canvasTarget.style.overflowY = "auto";
  }

  // Keyboard Positioning of Draggable Fields
  select_draggable_field(event) {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowLeft":
        event.preventDefault();
        if (event.target.classList.contains("selected")) {
          this.nudge_toolbox_item(event.target, event.key, event.shiftKey, event.ctrlKey);
        }
        break;
      case "ArrowDown":
      case "ArrowUp":
        event.preventDefault();
        if (event.target.classList.contains("selected")) {
          this.nudge_toolbox_item(event.target, event.key, event.shiftKey, event.ctrlKey);
        }
        break;
      case "Enter":
      case " ":
      case "Escape":
        event.preventDefault();
        this.handle_enter_or_space_case(event);
        break;
      case "Delete":
      case "Backspace":
        if (
          event.target.classList.contains("draggable-field") &&
          event.target.classList.contains("positioned")
        ) {
          event.preventDefault();
          event.target.remove();
        }
    }
  }

  setup_field_behavior() {
    this.keyboard_positioned_element.classList.add("positioned");

    this.pick_up_draggable_field_item(this.keyboard_positioned_element);
    this.make_element_resizable(this.keyboard_positioned_element);

    this.canvasTarget.appendChild(this.keyboard_positioned_element);
    this.keyboard_positioned_element.focus();

    this.keyboard_positioned_element.addEventListener("blur", (event) => {
      if (!event.relatedTarget || event.relatedTarget === event.target) {
        return;
      }

      requestAnimationFrame(() => {
        if (event.target.classList.contains("selected")) {
          this.position_keyboard_adjusted_element(event.target);
        }
      });
    });
  }

  pick_up_draggable_field_item(item) {
    this.deselect_toolbox_items();
    item.classList.add("selected");
  }

  position_keyboard_adjusted_element(target) {
    const { left, top } = target.getBoundingClientRect();
    let closest_overlay = this.get_closest_overlay(left, top);

    const { relative_x, relative_y, adjusted_height } = this.get_new_field_positions(target);

    this.keyboard_positioned_element.style.height = `${adjusted_height}rem`;
    this.add_draggable_element_styles(relative_x, relative_y);

    if (closest_overlay) {
      closest_overlay.appendChild(this.keyboard_positioned_element);
      this.sort_draggable_fields(closest_overlay);
    }
    if (this.keyboard_positioned_element.classList.contains("selected")) {
      this.keyboard_positioned_element.classList.remove("selected");
    }
    if (typeof this.keyboard_positioned_element.focus === "function") {
      this.keyboard_positioned_element.focus();
    }
  }

  sort_draggable_fields(closest_overlay) {
    const draggableElements = [...closest_overlay.querySelectorAll(".draggable-field")];
    draggableElements.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

    draggableElements.sort((a, b) => {
      const rectA = a.getBoundingClientRect();
      const rectB = b.getBoundingClientRect();

      return rectA.top - rectB.top || rectA.left - rectB.left;
    });

    draggableElements.forEach((el) => closest_overlay.appendChild(el));
  }

  handle_enter_or_space_case(event) {
    const root_font_size = this.get_root_font_size();
    if (event.target.classList.contains("selected")) {
      this.position_keyboard_adjusted_element(event.target);
    } else if (!event.target.classList.contains("selected")) {
      event.preventDefault();
      this.set_positioned_element(event, "keyboard");

      this.previously_postioned_element = event.target;

      this.offset_x = event.currentTarget.offsetLeft;
      this.offset_y = event.currentTarget.offsetTop;

      const scrolltop = this.canvasTarget.scrollTop / root_font_size;
      this.position_unselected_field(scrolltop);
      this.setup_field_behavior();
      this.setup_field_settings(this.keyboard_positioned_element);
    }
  }

  position_unselected_field(scrolltop) {
    const root_font_size = this.get_root_font_size();

    if (this.keyboard_positioned_element.classList.contains("positioned")) {
      const parent_overlay = this.keyboard_positioned_element.closest(".canvas_overlay");
      if (parent_overlay) {
        const overlay_rect = parent_overlay.getBoundingClientRect();
        const element_rect = this.keyboard_positioned_element.getBoundingClientRect();
        const relative_left = element_rect.left - overlay_rect.left;
        const relative_left_rem = relative_left / root_font_size;
        const offset = this.get_offset_top(this.keyboard_positioned_element, this.canvasTarget);

        this.keyboard_positioned_element.style.left = `${relative_left_rem + 1}rem`;
        this.keyboard_positioned_element.style.top = `${offset / root_font_size}rem`;
        this.keyboard_positioned_element.style.opacity = "0.7";
      }
    } else {
      this.keyboard_positioned_element.style.left = `10rem`;
      this.keyboard_positioned_element.style.top = `${scrolltop + 10}rem`;
    }
  }

  get_new_field_positions(target) {
    const root_font_size = this.get_root_font_size();

    const { left, top, height } = target.getBoundingClientRect();
    let closest_overlay = this.get_closest_overlay(left, top);

    const overlay_rect = closest_overlay.getBoundingClientRect();
    const relative_x = left - overlay_rect.left;
    let relative_y = top - overlay_rect.top;

    const overlay_bottom = overlay_rect.top + overlay_rect.height;
    let adjusted_height = height;

    if (top + height > overlay_bottom) {
      adjusted_height = Math.max(overlay_bottom - top, 2 * root_font_size);
    }

    if (relative_y + adjusted_height > overlay_rect.height) {
      relative_y = relative_y - adjusted_height;
    }

    if (relative_y < 0) {
      relative_y = 0;
    }

    return {
      relative_x: relative_x / root_font_size,
      relative_y: relative_y / root_font_size,
      adjusted_height: adjusted_height / root_font_size,
    };
  }

  get_closest_overlay(field_x, field_y) {
    const overlay_divs = [...this.canvasTarget.querySelectorAll(".canvas_overlay")];
    let closest_overlay = null;
    let min_distance = Infinity;

    overlay_divs.forEach((overlay) => {
      const overlay_rect = overlay.getBoundingClientRect();
      const overlay_center_x = overlay_rect.left + overlay_rect.width / 2;
      const overlay_center_y = overlay_rect.top + overlay_rect.height / 2;

      const distance = Math.sqrt(
        Math.pow(field_x - overlay_center_x, 2) + Math.pow(field_y - overlay_center_y, 2),
      );

      if (distance < min_distance) {
        min_distance = distance;
        closest_overlay = overlay;
      }
    });
    return closest_overlay;
  }

  add_draggable_element_styles(relative_x, relative_y) {
    this.keyboard_positioned_element.style.position = "absolute";
    this.keyboard_positioned_element.style.left = `${relative_x}rem`;
    this.keyboard_positioned_element.style.top = `${relative_y}rem`;
    this.keyboard_positioned_element.style.opacity = "1";
    this.keyboard_positioned_element.style.cursor = "grab";
  }

  get_offset_top(child, ancestor) {
    let offset = 0;
    let element = child;

    while (element) {
      offset += element.offsetTop;
      element = element.offsetParent;
      if (element === ancestor) {
        return offset;
      }
    }

    return offset;
  }

  nudge_toolbox_item(item, direction, shift_key, ctrlKey) {
    if (shift_key) return;

    let nudge = 0.5;
    if (ctrlKey) {
      nudge = 1.5;
    }

    let current_left = parseFloat(item.style.left) || 0;
    let current_top = parseFloat(item.style.top) || 0;

    const item_rect = item.getBoundingClientRect();
    const canvas_rect = this.canvasTarget.getBoundingClientRect();

    if (this.is_out_of_bounds(item_rect, canvas_rect, direction, false)) {
      return;
    }

    switch (direction) {
      case "ArrowRight":
        item.style.left = `${current_left + nudge}rem`;
        break;
      case "ArrowLeft":
        item.style.left = `${current_left - nudge}rem`;
        break;
      case "ArrowDown":
        item.style.top = `${current_top + nudge}rem`;
        break;
      case "ArrowUp":
        item.style.top = `${current_top - nudge}rem`;
        break;
    }

    this.ensure_element_visible(item, ctrlKey);
    this.updateTagSettings();
  }

  handle_keyboard_resize(event, element) {
    const root_font_size = this.get_root_font_size();
    if (event.shiftKey === false) return;

    const current_height = parseFloat(element.clientHeight) / root_font_size;
    if (
      this.is_out_of_bounds(
        element.getBoundingClientRect(),
        this.canvasTarget.getBoundingClientRect(),
        event.key,
        event.shiftKey,
      )
    )
      return;

    switch (event.key) {
      case "ArrowRight":
        if (event.target.classList.contains("selected")) {
          element.style.width = `${parseFloat(event.target.clientWidth) / root_font_size + 1}rem`;
        }
        break;
      case "ArrowLeft":
        if (event.target.classList.contains("selected")) {
          element.style.width = `${parseFloat(event.target.clientWidth) / root_font_size - 1}rem`;
        }
        break;
      case "ArrowDown":
        if (event.target.classList.contains("selected")) {
          element.style.height = `${parseFloat(event.target.clientHeight) / root_font_size + 1}rem`;
        }
        break;
      case "ArrowUp":
        if (event.target.classList.contains("selected")) {
          element.style.height = `${Math.max(current_height - 1, 2)}rem`;
        }
        break;
    }
  }

  is_out_of_bounds(element_rect, canvas_rect, direction, shift_key) {
    if (shift_key) {
      direction += " + Shift";
    }

    const margin_right = 30;
    const margin_left = 30;
    const margin_bottom = 40;
    const margin_top = 40;

    switch (direction) {
      case "ArrowRight":
      case "ArrowRight + Shift":
        return element_rect.left + element_rect.width >= canvas_rect.right - margin_right;
      case "ArrowLeft":
        return element_rect.left <= canvas_rect.left + margin_left;
      case "ArrowDown":
      case "ArrowDown + Shift":
        return element_rect.top + element_rect.height >= canvas_rect.bottom - margin_bottom;
      case "ArrowUp":
        return element_rect.top <= canvas_rect.top + margin_top;
      default:
        return false;
    }
  }

  ensure_element_visible(element, ctrlKey) {
    const element_rect = element.getBoundingClientRect();
    const canvas_rect = this.canvasTarget.getBoundingClientRect();

    const scroll_trigger = 150;
    let scroll_amount = 150;
    if (ctrlKey) {
      scroll_amount = 450;
    }

    if (element_rect.top < canvas_rect.top + scroll_trigger) {
      this.canvasTarget.scrollBy({ top: -scroll_amount, behavior: "smooth" });
    } else if (element_rect.bottom > canvas_rect.bottom - scroll_trigger) {
      this.canvasTarget.scrollBy({ top: scroll_amount, behavior: "smooth" });
    }
  }

  deselect_toolbox_items() {
    this.element.querySelectorAll(".selected").forEach((el) => el.classList.remove("selected"));
  }

  // Keyboard Navigation of Page Previews
  navigate_thumbnails(event) {
    const thumbnails = [...this.pagePreviewTarget.querySelectorAll(".page-preview-item")];

    if (thumbnails.length === 0) return;

    let current_index = thumbnails.findIndex((thumb) => thumb.classList.contains("selected"));

    switch (event.key) {
      case "ArrowUp":
        event.preventDefault();
        if (current_index > 0) this.select_thumbnail(thumbnails[current_index - 1]);
        if (current_index >= 0) thumbnails[current_index - 1].click();
        break;
      case "ArrowDown":
        event.preventDefault();
        if (current_index < thumbnails.length - 1)
          this.select_thumbnail(thumbnails[current_index + 1]);
        if (current_index >= 0 && thumbnails[current_index + 1])
          thumbnails[current_index + 1].click();
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        if (current_index >= 0) thumbnails[current_index].click();
        break;
    }
  }

  // Mouse and Touch Resize Draggable Element
  make_element_resizable(element) {
    if (element.querySelector(".resize-handle")) {
      return;
    }

    const resize_handle = this.create_resize_handle(element);
    resize_handle.addEventListener("mousedown", (event) => this.start_resize(event, element));
    resize_handle.addEventListener("touchstart", (event) => this.start_resize(event, element), {
      passive: false,
    });

    element.addEventListener("keydown", (event) => this.handle_keyboard_resize(event, element));
  }

  start_resize(event, element) {
    event.preventDefault();
    this.is_resizing = true;
    this.resizing_element = element;
    this.canvasTarget.style.overflowY = "hidden";

    document.addEventListener("mousemove", this.resize);
    document.addEventListener("mouseup", this.stop_resize);
    document.addEventListener("touchmove", this.resize, { passive: false });
    document.addEventListener("touchend", this.stop_resize);
  }

  create_resize_handle(element) {
    const resize_handle = document.createElement("div");
    resize_handle.classList.add("resize-handle");
    element.appendChild(resize_handle);
    const resize_handle_child = document.createElement("div");
    resize_handle.appendChild(resize_handle_child);
    return resize_handle;
  }

  resize = (event) => {
    if (!this.is_resizing || !this.resizing_element) return;

    const { client_x, client_y } = this.get_pointer_coordinates(event);
    const min_height = this.calculate_min_field_height();
    const { new_width, new_height } = this.calculate_new_field_size(client_x, client_y, min_height);
    this.apply_new_field_size(new_width, new_height);
  };

  get_pointer_coordinates(event) {
    let is_touch = event.type.startsWith("touch");
    return {
      client_x: is_touch ? event.touches[0].clientX : event.clientX,
      client_y: is_touch ? event.touches[0].clientY : event.clientY,
    };
  }

  calculate_min_field_height() {
    const computed_style = window.getComputedStyle(this.resizing_element);
    const font_size = parseFloat(computed_style.fontSize);
    const padding_top = parseFloat(computed_style.paddingTop);
    const padding_bottom = parseFloat(computed_style.paddingBottom);
    const line_height =
      computed_style.lineHeight === "normal"
        ? font_size * 1.2
        : parseFloat(computed_style.lineHeight);
    return line_height + padding_top + padding_bottom;
  }

  calculate_new_field_size(client_x, client_y, min_height) {
    const resizing_element_rect = this.resizing_element.getBoundingClientRect();
    const container_rect = this.resizing_element.parentElement.getBoundingClientRect();

    let new_width = client_x - resizing_element_rect.left;
    let new_height = Math.max(client_y - resizing_element_rect.top, min_height);

    const max_width = container_rect.width - (resizing_element_rect.left - container_rect.left);
    const max_height = container_rect.height - (resizing_element_rect.top - container_rect.top);

    new_width = Math.min(new_width, max_width);
    new_height = Math.min(new_height, max_height);
    return { new_width, new_height };
  }

  apply_new_field_size(new_width, new_height) {
    const root_font_size = this.get_root_font_size();
    this.resizing_element.style.width = `${new_width / root_font_size}rem`;
    this.resizing_element.style.height = `${new_height / root_font_size}rem`;
  }

  stop_resize = () => {
    this.resizing_element = null;
    this.canvasTarget.style.overflowY = "auto";

    document.removeEventListener("mousemove", this.resize);
    document.removeEventListener("mouseup", this.stop_resize);
    document.removeEventListener("touchmove", this.resize);
    document.removeEventListener("touchend", this.stop_resize);
  };

  // Save point data
  async capture_field_data(event) {
    event.preventDefault();

    if (!this.canvasTarget) {
      console.error("PDF Viewer not found");
      return;
    }

    const result_set = this.set_pdf_data();

    const fieldName = this.formTarget.dataset.documentTaggingFieldName;

    this.add_hidden_field_to_form(fieldName, JSON.stringify(result_set));
    this.close_modal();

    // Show the save template modal after tagging is complete
    await this.showSaveTemplateModal();
  }

  async showSaveTemplateModal() {
    // Find the save template modal
    const modalElement = document.getElementById("save-template-modal");
    if (!modalElement) {
      // No modal found, continue without template saving
      return;
    }

    const modalController = this.application.getControllerForElementAndIdentifier(
      modalElement,
      "save-template-modal",
    );

    if (!modalController) {
      // Modal controller not found, continue without template
      return;
    }

    // Show the modal and wait for user response
    const templateName = await modalController.show(this.formTarget);

    // Store the template name (or null/undefined) for later use
    // undefined means cancelled - we'll treat it as null (don't save)
    this.templateNameToSave = templateName === undefined ? null : templateName;
  }

  set_pdf_data() {
    const result_set = [];
    const pdfs = this.canvasTarget.querySelectorAll(".pdf-wrapper");

    pdfs.forEach((pdf) => {
      const file_name = pdf.dataset.fileName;
      const pages = pdf.querySelectorAll("div[data-page-number]");
      const pdf_data = {};
      const canvas = pdf.querySelector("canvas");

      pdf_data["filename"] = file_name;
      pdf_data["pages"] = {};
      pages.forEach((pageDiv) => {
        this.set_page_data(pageDiv, canvas, pdf_data);
      });

      result_set.push(pdf_data);
    });
    return result_set;
  }

  set_page_data(page_div, canvas, pdf_data) {
    const page_number = parseInt(page_div.dataset.pageNumber, 10);
    const draggable_fields = page_div.querySelectorAll("span.draggable-field");

    const ratio = parseFloat(page_div.dataset.ratio);
    const pdf_scale = parseFloat(page_div.dataset.pdfScale);

    const fields = [];
    const field_count = {};

    draggable_fields.forEach((field) => {
      const field_type = field.dataset.draggableFieldType;
      field_count[field_type] = ++field_count[field_type] || 1;
      const field_data = this.create_field_data(
        ratio,
        pdf_scale,
        canvas,
        field,
        field_type,
        page_number,
        field_count,
        page_div,
      );
      fields.push(field_data);

      pdf_data["pages"][`page-${page_number}`] = fields;
    });
  }

  create_field_data(
    ratio,
    pdf_scale,
    canvas,
    field,
    field_type,
    page_number,
    field_count,
    page_div,
  ) {
    const { top, left, height, width } = this.get_field_dimensions(field);
    const { abs_top, abs_left, abs_width, abs_height } = this.calculate_values(
      top,
      ratio,
      pdf_scale,
      left,
      height,
      width,
    );
    const { pct_top, pct_left, pct_width, pct_height } = this.calculate_percent_values(
      abs_top,
      abs_left,
      abs_height,
      abs_width,
      page_div,
      pdf_scale,
    );

    const dataset = field.dataset;
    return {
      id: `${field_type}-${page_number}-${field_count[field_type]}`,
      type: dataset.draggableFieldType,
      pct_values: { ystart: pct_top, xstart: pct_left, width: pct_width, height: pct_height },
      abs_values: { top: abs_top, left: abs_left, width: abs_width, height: abs_height },
      custom_id: dataset.customId,
      placeholder: dataset.placeholder,
    };
  }

  calculate_percent_values(top, left, height, width, page_div, pdf_scale) {
    const viewport_width = parseFloat(page_div.dataset.viewportWidth);
    const viewport_height = parseFloat(page_div.dataset.viewportHeight);

    const pct_top = ((top * pdf_scale) / viewport_height) * 100;
    const pct_left = ((left * pdf_scale) / viewport_width) * 100;
    const pct_height = ((height * pdf_scale) / viewport_height) * 100;
    const pct_width = ((width * pdf_scale) / viewport_width) * 100;
    return { pct_top, pct_left, pct_width, pct_height };
  }

  calculate_values(top, ratio, pdf_scale, left, height, width) {
    const abs_top = (top * ratio) / pdf_scale;
    const abs_left = (left * ratio) / pdf_scale;
    const abs_height = (height * ratio) / pdf_scale;
    const abs_width = (width * ratio) / pdf_scale;
    return { abs_top, abs_left, abs_width, abs_height };
  }

  get_field_dimensions(field) {
    const root_font_size = this.get_root_font_size();
    const top = parseFloat(field.style.top) * root_font_size;
    const left = parseFloat(field.style.left) * root_font_size;
    const width = parseFloat(field.style.width) * root_font_size || field.offsetWidth;
    const height = parseFloat(field.style.height) * root_font_size || field.offsetHeight;
    return { top, left, height, width };
  }

  add_hidden_field_to_form(fieldName, value) {
    if (!this.formTarget) {
      console.error(`Form target not found.`);
      return;
    }

    let hidden_field = this.formTarget.querySelector(`input[name="${fieldName}"]`);

    if (hidden_field) {
      hidden_field.value = value;
    } else {
      hidden_field = document.createElement("input");
      hidden_field.type = "hidden";
      hidden_field.name = fieldName;
      hidden_field.value = value;
      this.formTarget.appendChild(hidden_field);
    }
  }

  restore_tags_for_page(overlay, file_name, page_num) {
    if (!this.existing_tagging_data || !Array.isArray(this.existing_tagging_data)) {
      return;
    }

    // Find the tagging data for this specific file
    const file_data = this.existing_tagging_data.find((data) => data.filename === file_name);
    if (!file_data || !file_data.pages) {
      return;
    }

    // Get the fields for this specific page
    const page_key = `page-${page_num}`;
    const page_fields = file_data.pages[page_key];
    if (!page_fields || !Array.isArray(page_fields)) {
      return;
    }

    // Create and position each field
    page_fields.forEach((field_data) => {
      this.create_tag_from_data(overlay, field_data);
    });
  }

  create_tag_from_data(overlay, field_data) {
    const root_font_size = this.get_root_font_size();

    // Create the draggable field element
    const field = document.createElement("span");
    field.classList.add("draggable-field", "positioned");
    field.tabIndex = 0;
    field.dataset.draggableFieldType = field_data.type;

    // Set custom data attributes if they exist
    if (field_data.custom_id) {
      field.dataset.customId = field_data.custom_id;
    }
    if (field_data.placeholder) {
      field.dataset.placeholder = field_data.placeholder;
    }

    // Set the display text based on field type
    if (field_data.type === "custom" && field_data.placeholder) {
      field.innerText = field_data.placeholder;
    } else {
      field.innerText = field_data.type;
    }

    // Position and size the field using percentage values
    const pct = field_data.pct_values;
    const viewport_width = parseFloat(overlay.dataset.viewportWidth);
    const viewport_height = parseFloat(overlay.dataset.viewportHeight);
    const ratio = parseFloat(overlay.dataset.ratio);
    // const pdf_scale = parseFloat(overlay.dataset.pdfScale);

    // Convert percentage back to pixel positions
    const left_px = (pct.xstart * viewport_width) / (100 * ratio);
    const top_px = (pct.ystart * viewport_height) / (100 * ratio);
    const width_px = (pct.width * viewport_width) / (100 * ratio);
    const height_px = (pct.height * viewport_height) / (100 * ratio);

    // Apply styles
    field.style.position = "absolute";
    field.style.left = `${left_px / root_font_size}rem`;
    field.style.top = `${top_px / root_font_size}rem`;
    field.style.width = `${width_px / root_font_size}rem`;
    field.style.height = `${height_px / root_font_size}rem`;
    field.style.opacity = "1";
    field.style.cursor = "grab";

    // Make the field resizable
    this.make_element_resizable(field);

    // Append to the overlay
    overlay.appendChild(field);
  }
}
